import { Flex, Row, Space, Tooltip, Typography } from 'antd';
import React from 'react';
import styled from 'styled-components';
import type {
  ReconDetailsBankRecord,
  RecordType
} from '@melio/payments-recon-api-client-ts';
import { TransactionsourceEnumType } from '@tools/ui/types/generated/graphql';
import { theme } from '@tools/ui-utils/theme.util';
import { t } from '@tools/ui-utils/translate.util';
import {
  AmountWithCurrency,
  DateSnippet
} from '../../../../components/snippets';
import TagStatus from '../../../../components/snippets/tagStatus';
import { last4Digits } from '../../../../utils/last-4-digits';
import { reconEnumValue } from '../../../../utils/reconEnumValue';
import { UnmatchRecord } from '../actions/unmatch-record';
import FundsDirection from '../fundsDirection';
import LinkToPayments from '../link-to-payments';
import TransferIdsCell from '../transfer-ids-cell';

const Label = styled(Typography.Text)`
  display: block;
  margin-bottom: 4px;
  color: ${theme.colors.verificationTask.isRevived};
`;

export const BankRecordDetails: React.FC<{
  bankRecord: ReconDetailsBankRecord & {
    extraDetails?: {
      rejectStatus?: string;
    };
  };
  refetch: () => void;
}> = ({ bankRecord, refetch }) => {
  return (
    <Space direction="vertical" size={20}>
      <div>
        <Label>Reco ID</Label>
        {bankRecord.recordId}
      </div>

      <div>
        <Label>Reconciliation Status</Label>
        {bankRecord.reconciliationStatus}
      </div>

      <div>
        <Label>{t('reco.tableColumns.type')}</Label>
        {bankRecord.bankRecordType}
      </div>

      <div>
        <Label>{t('reco.transactionDate')}</Label>
        <DateSnippet
          date={bankRecord.transactionDate!.toString()}
          format="MMM DD, YYYY"
        />
      </div>

      <div>
        <Label>{t('reco.amount')}</Label>
        <Row>
          <FundsDirection direction={bankRecord.direction!} />
          &nbsp;&nbsp;
          <AmountWithCurrency amount={bankRecord.amount} />
        </Row>
      </div>

      <div>
        <Label>{t('reco.tableColumns.bank')}</Label>
        <TagStatus
          status={bankRecord.financialInstitution as TransactionsourceEnumType}
        />
      </div>

      <div>
        <Label>{t('reco.tableColumns.account')}</Label>
        <Tooltip
          placement="top"
          title={bankRecord.accountNumber}
          trigger={'click'}
        >
          <span style={{ cursor: 'pointer' }}>
            ...{last4Digits(bankRecord.accountNumber)}
          </span>
        </Tooltip>
      </div>

      <div>
        <Label>{t('reco.tableColumns.description')}</Label>
        {bankRecord.description || 'N/A'}
      </div>

      {!!bankRecord?.checkSerialNumber && (
        <div>
          <Label>{t('reco.tableColumns.sn')}</Label>
          {bankRecord.checkSerialNumber}
        </div>
      )}

      {!!bankRecord?.paymentId && (
        <div>
          <Label>Matched Payment</Label>
          <Flex align="center" gap={12}>
            <LinkToPayments bankRecord={bankRecord}></LinkToPayments>
            <UnmatchRecord
              recordId={bankRecord.recordId!}
              recordType={reconEnumValue<RecordType>('bank')}
              onComplete={refetch}
            />
          </Flex>
        </div>
      )}

      {!!bankRecord?.transferIds && (
        <div>
          <Label>Matched Transfers</Label>
          <TransferIdsCell bankRecord={bankRecord} />
        </div>
      )}

      {!!bankRecord?.classification && (
        <div>
          <Label>{t('reco.tableColumns.classification')}</Label>
          {bankRecord.classification}
        </div>
      )}

      {!!bankRecord?.manualChangesReason && (
        <div>
          <Label>{t('reco.tableColumns.reason')}</Label>
          {bankRecord.manualChangesReason}
        </div>
      )}

      {!!bankRecord?.comment && (
        <div>
          <Label>{t('reco.tableColumns.comment')}</Label>
          {bankRecord.comment}
        </div>
      )}
    </Space>
  );
};
